import { limit, orderBy, query } from 'firebase/firestore';
import { useMemo } from 'react';
import { useCollection, useDoc } from '@/lib/firestore/hooks';
import { workOrderDoc, workOrdersCol, workOrderTasksCol } from '@/lib/firestore/refs';
import type { WorkOrder, WorkOrderState, WorkOrderTask } from '@shared/types';

const OPEN = new Set<WorkOrderState>(['Draft', 'Assigned', 'Dispatched', 'Work In Progress', 'On Hold']);

/** Recent work orders ordered by update; state filtering is client-side (no composite index needed). */
export function useWorkOrders(state: 'All' | 'Open' | WorkOrderState) {
  const { data, loading } = useCollection<WorkOrder>(useMemo(() => query(workOrdersCol(), orderBy('updatedAt', 'desc'), limit(500)), []), []);
  const filtered = useMemo(() => {
    if (state === 'All') return data;
    if (state === 'Open') return data.filter((w) => OPEN.has(w.state));
    return data.filter((w) => w.state === state);
  }, [data, state]);
  return { data: filtered, loading };
}
export const useWorkOrder = (id: string | null) => useDoc<WorkOrder>(id ? workOrderDoc(id) : null);
export const useWorkOrderTasks = (id: string | null) => useCollection<WorkOrderTask>(useMemo(() => (id ? query(workOrderTasksCol(id), orderBy('order')) : null), [id]), [id]);
export function useCompanyWorkOrders(companyId: string | null) {
  const { data, loading } = useCollection<WorkOrder>(useMemo(() => query(workOrdersCol(), orderBy('updatedAt', 'desc'), limit(500)), []), []);
  const filtered = useMemo(() => (companyId ? data.filter((w) => w.companyId === companyId) : []), [data, companyId]);
  return { data: filtered, loading };
}
